// Secret-scanning on topic write — the single ruleset every surface shares.
//
// Topic content is NOT encrypted at rest (it would break full-text search, the
// core of Driftless), so the control that keeps pasted credentials out of the
// team knowledge graph is detection-on-write: block the write, with an explicit
// override. The API enforces it as the authoritative gate (an agent via MCP or
// the raw API must be blocked — we don't control those clients); the CLI runs
// the same scan locally so a secret never leaves the machine on that path.
//
// Design bias: HIGH PRECISION. A false positive blocks a legitimate write and
// erodes trust in the whole layer, so the ruleset is curated, well-known
// provider formats + private keys + Driftless's own credentials — not a generic
// high-entropy heuristic (that lives behind a future flag). Anchor/label fields
// (patterns, tags, where_files) are never scanned: they hold globs and human
// labels, where a long token-shaped path would false-positive.

export interface SecretFinding {
  /** The topic field the match was found in (e.g. 'content', 'gotchas'). */
  field: string
  /** Human name of the rule that matched (e.g. 'AWS access key id'). */
  rule: string
  /** MASKED preview of the match — never the full secret. */
  preview: string
}

interface SecretRule {
  rule: string
  // Global flag so a single field with several secrets surfaces them all.
  regex: RegExp
}

// Each rule targets a distinctive, low-false-positive credential shape. Order is
// irrelevant — every rule runs against every field.
const RULES: SecretRule[] = [
  { rule: 'Private key block', regex: /-----BEGIN (?:RSA |EC |OPENSSH |DSA |PGP )?PRIVATE KEY-----/g },
  { rule: 'AWS access key id', regex: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g },
  { rule: 'GitHub token', regex: /\b(?:ghp|gho|ghu|ghs|ghr)_[0-9A-Za-z]{36}\b/g },
  { rule: 'GitHub fine-grained PAT', regex: /\bgithub_pat_[0-9A-Za-z_]{60,}\b/g },
  { rule: 'Anthropic API key', regex: /\bsk-ant-[A-Za-z0-9_-]{20,}\b/g },
  // OpenAI keys: a KNOWN prefix (`proj-`/`svcacct-`/`admin-`) then a long run, or
  // a legacy `sk-`+~48 ALPHANUMERIC (no hyphens). Hyphens are allowed ONLY right
  // after an enumerated prefix — a kebab slug like `sk-learn-preprocessing-pipeline`
  // false-positives otherwise. The 40-char floor keeps ordinary prose out, and
  // enumerating prefixes (vs `[a-z]+-`) keeps `sk-ant-…` and long kebab out.
  { rule: 'OpenAI API key', regex: /\bsk-(?:(?:proj|svcacct|admin)-[A-Za-z0-9_-]{40,}|[A-Za-z0-9]{40,})\b/g },
  { rule: 'Stripe secret key', regex: /\b(?:sk|rk)_(?:live|test)_[0-9A-Za-z]{20,}\b/g },
  { rule: 'Stripe webhook secret', regex: /\bwhsec_[0-9A-Za-z]{20,}\b/g },
  { rule: 'Slack token', regex: /\bxox[baprs]-[0-9A-Za-z-]{10,}\b/g },
  { rule: 'Google API key', regex: /\bAIza[0-9A-Za-z_-]{35}\b/g },
  { rule: 'GitLab PAT', regex: /\bglpat-[0-9A-Za-z_-]{20}\b/g },
  { rule: 'SendGrid API key', regex: /\bSG\.[\w-]{22}\.[\w-]{43}\b/g },
  { rule: 'npm token', regex: /\bnpm_[0-9A-Za-z]{36}\b/g },
  // Driftless's own credentials — a user pasting a live key/token into a topic
  // is the most likely self-inflicted leak. drift_oat_/ort_/rat_/code_ are the
  // typed prefixes; drift_<12hex> is an API key (see api-key.service).
  { rule: 'Driftless credential', regex: /\bdrift_(?:oat|ort|rat|code)_[A-Za-z0-9_-]{16,}\b/g },
  { rule: 'Driftless API key', regex: /\bdrift_[0-9a-f]{32,}\b/g },
  // JWT — three base64url segments. Medium confidence, but the eyJ... header is
  // distinctive enough that a real-world false positive is rare.
  { rule: 'JWT', regex: /\beyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g },
]

// Show enough to recognize which credential leaked, never enough to use it.
function mask(match: string): string {
  const head = match.slice(0, 4)
  return `${head}${'*'.repeat(Math.min(8, Math.max(3, match.length - 4)))}`
}

function flatten(value: string | string[] | null | undefined): string[] {
  if (value == null) return []
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [value]
}

/**
 * Scan a set of named topic fields for likely secrets. Returns one finding per
 * (field, rule, match); an empty array means clean. Pure and deterministic —
 * no I/O, no global state. The `preview` on each finding is masked, so the
 * result can be surfaced to a user or written to a log without leaking the key.
 */
export function scanForSecrets(
  fields: Record<string, string | string[] | null | undefined>,
): SecretFinding[] {
  const findings: SecretFinding[] = []
  for (const [field, raw] of Object.entries(fields)) {
    for (const text of flatten(raw)) {
      if (!text) continue
      for (const { rule, regex } of RULES) {
        // Fresh lastIndex per field/text — these regexes are module-level and
        // global, so reset before each use.
        regex.lastIndex = 0
        let m: RegExpExecArray | null
        while ((m = regex.exec(text)) !== null) {
          findings.push({ field, rule, preview: mask(m[0]) })
          // Guard against zero-length matches looping forever (none here, but cheap).
          if (m.index === regex.lastIndex) regex.lastIndex++
        }
      }
    }
  }
  return findings
}

/**
 * One-line, human-readable summary of findings for an error message. Safe to
 * surface — uses only masked previews.
 */
export function formatSecretFindings(findings: SecretFinding[]): string {
  return findings.map((f) => `${f.rule} in ${f.field} (${f.preview})`).join('; ')
}

// THE canonical set of persisted topic text fields a secret could hide in —
// the single source of truth shared by the server gate and the CLI pre-flight.
// Anchor/label fields (patterns, tags, where_files, where_repos) are
// deliberately absent: they hold globs and labels where a token-shaped path
// would false-positive. Add a new free-text column here and EVERY write surface
// scans it — there is no second list to keep in sync.
export const SCANNABLE_TEXT_FIELDS = [
  'name', 'title', 'what', 'how', 'decisions', 'gotchas', 'ownership',
  'content', 'file_content', 'anchored_doc_path', 'stale_reason',
  'invariants', 'required_checks',
] as const

// Update-path append params alias onto the same fields (a single appended
// gotcha must be scanned exactly like a replaced one). Labelled by their
// singular intent so a finding reads naturally.
const APPEND_ALIASES: Record<string, string> = {
  _append_gotchas: 'gotcha',
  _append_decisions: 'decision',
  _append_invariants: 'invariant',
  _append_required_checks: 'check',
}

/**
 * Scan a topic write payload (create body, update patch, or CLI request body)
 * for secrets. Picks exactly the canonical text fields + append params from
 * whatever shape it's given — so the server gate and every CLI path scan the
 * same thing by construction, and a new text field can't silently bypass the
 * scan at some call sites. Returns findings (empty = clean).
 */
export function scanWritePayload(payload: Record<string, unknown>): SecretFinding[] {
  const fields: Record<string, string | string[] | null | undefined> = {}
  for (const f of SCANNABLE_TEXT_FIELDS) {
    const v = payload[f]
    if (v != null) fields[f] = v as string | string[]
  }
  for (const [key, label] of Object.entries(APPEND_ALIASES)) {
    const v = payload[key]
    if (v != null) fields[label] = v as string | string[]
  }
  // ARP v2 — anchors[] and rules[] are JSONB arrays of objects. A pasted key in
  // any of their string values (e.g. anchor.auth, anchor.value, rule.action) must
  // be caught too, so flatten every string value into scannable buckets.
  const anchorStrings = collectStrings(payload['anchors'])
  if (anchorStrings.length) fields['anchor'] = anchorStrings
  const ruleStrings = collectStrings(payload['rules'])
  if (ruleStrings.length) fields['rule'] = ruleStrings
  return scanForSecrets(fields)
}

// Flatten every string value out of an array of plain objects (one level of
// nested arrays handled, e.g. rule.fields[]). Non-arrays / non-objects → [].
function collectStrings(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  const out: string[] = []
  for (const item of v) {
    if (item && typeof item === 'object') {
      for (const val of Object.values(item as Record<string, unknown>)) {
        if (typeof val === 'string') out.push(val)
        else if (Array.isArray(val)) for (const x of val) if (typeof x === 'string') out.push(x)
      }
    }
  }
  return out
}
